import { native } from '@forest/native-services'
import { useStore } from '@nanostores/solid'
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  Show,
  type JSX,
} from 'solid-js'
import { Portal } from 'solid-js/web'
import { shopCatalog } from '~/config'
import { useLocalizedTreeName } from '~/hooks/use-localized-tree-name'
import { deriveShopCatalog, type ShopItem } from '~/lib/derive-shop-catalog'
import { m } from '~/paraglide/messages'
import { useProducts } from '~/resources/products'
import { $isInSubscription } from '~/store/service-level'
import { PlusBadge } from './plus-badge'
import { ShopItemCard } from './shop-item-card'

export type ShopTabValue = 'bakery' | 'plus'

export function ExchangeShop(props: {
  unlockedTreeIds: Set<number>
  isRedeemPending: boolean
  /** `onSettled` must be called once the redeem attempt concludes (success,
   *  insufficient stamps, or error) — it dismisses the confirm dialog. */
  onRedeem: (item: ShopItem, onSettled: () => void) => void
  onUpgrade: () => void
}) {
  const [activeTab, setActiveTab] = createSignal<ShopTabValue>('bakery')
  const [pendingItem, setPendingItem] = createSignal<ShopItem | null>(null)
  const productsQuery = useProducts()
  const isInSubscription = useStore($isInSubscription)

  // Fires on shop open (initial tab = bakery) and on every tab switch.
  createEffect(() => {
    native.general.logEvent({
      event_name: 'page_view',
      event_params: {
        page_name:
          activeTab() === 'bakery'
            ? '2026_birthday_token_store_cake_trees'
            : '2026_birthday_token_store_comeback_trees',
      },
    })
  })

  const items = createMemo(() =>
    deriveShopCatalog({
      config: shopCatalog,
      products: productsQuery.data ?? [],
      unlockedTreeIds: props.unlockedTreeIds,
      isPlus: isInSubscription(),
      tab: activeTab(),
    }),
  )

  const dismissDialog = () => setPendingItem(null)

  const handleConfirmRedeem = () => {
    const item = pendingItem()
    if (!item) return
    props.onRedeem(item, dismissDialog)
  }

  return (
    <div class="relative flex h-full w-full flex-col overflow-hidden bg-bakery-cream">
      <div
        aria-hidden
        class="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#BEE7E6] to-[#C3F8C7] transition-opacity duration-300"
        classList={{
          'opacity-100': activeTab() === 'plus',
          'opacity-0': activeTab() !== 'plus',
        }}
      />

      <div class="relative z-10 flex-1 overflow-y-auto pt-[140px]">
        <ShopHeader activeTab={activeTab()} onChange={setActiveTab} />

        <InfoAccordion tab={activeTab()} />

        <div class="mx-auto flex max-w-[640px] flex-wrap justify-center gap-2 px-1 pb-12 pt-3">
          <For each={items()}>
            {(item) => (
              <ShopItemCard
                item={item}
                onRedeem={() => setPendingItem(item)}
                onUpgrade={props.onUpgrade}
              />
            )}
          </For>
        </div>
      </div>

      <Show when={pendingItem()}>
        {(item) => (
          <RedeemConfirmDialog
            item={item()}
            isRedeemPending={props.isRedeemPending}
            onCancel={dismissDialog}
            onConfirm={handleConfirmRedeem}
          />
        )}
      </Show>
    </div>
  )
}

function ShopHeader(props: {
  activeTab: ShopTabValue
  onChange: (v: ShopTabValue) => void
}) {
  return (
    <div class="flex flex-col items-center gap-4 px-6 pb-4">
      <h1 class="w-full text-center text-[24px] font-bold leading-[34px] text-bakery-brown">
        {m.exchange_shop_title()}
      </h1>
      <div class="flex items-center gap-1">
        <FilterPill
          active={props.activeTab === 'bakery'}
          onClick={() => props.onChange('bakery')}
        >
          <span
            class="text-sm font-semibold"
            classList={{
              'text-bakery-cream': props.activeTab === 'bakery',
              'text-[#8c734d]': props.activeTab !== 'bakery',
            }}
          >
            {m.shop_tab_cakes()}
          </span>
        </FilterPill>
        <FilterPill
          active={props.activeTab === 'plus'}
          onClick={() => props.onChange('plus')}
        >
          <PlusBadge />
          <span
            class="text-sm font-semibold"
            classList={{
              'text-bakery-cream': props.activeTab === 'plus',
              'text-[#8c734d]': props.activeTab !== 'plus',
            }}
          >
            {m.shop_tab_past_trees()}
          </span>
        </FilterPill>
      </div>
    </div>
  )
}

function FilterPill(props: {
  active: boolean
  onClick: () => void
  children: JSX.Element
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      class="flex items-center gap-2 rounded-[36px] px-3 py-2 backdrop-blur-[15px] transition-colors duration-150"
      classList={{
        'bg-[#8e5c41]': props.active,
        'bg-white/[0.64] active:bg-white/[0.85]': !props.active,
      }}
    >
      {props.children}
    </button>
  )
}

function InfoAccordion(props: { tab: ShopTabValue }) {
  const [open, setOpen] = createSignal(false)
  const title = () =>
    props.tab === 'bakery'
      ? m.how_stamps_work_title()
      : m.how_to_redeem_tree_title()
  const bullets = () =>
    props.tab === 'bakery'
      ? [
          m.how_stamps_work_list1(),
          m.how_stamps_work_list2(),
          m.how_stamps_work_list3(),
        ]
      : [m.how_to_redeem_tree_list1(), m.how_to_redeem_tree_list2()]

  return (
    <div class="mx-auto w-full max-w-[640px] px-6">
      <button
        type="button"
        aria-expanded={open()}
        onClick={() => setOpen((v) => !v)}
        class="flex w-full items-center gap-2 rounded-[14px] px-2 py-2 text-left text-[#856245] transition-colors duration-150 active:bg-black/5"
      >
        <svg
          viewBox="0 0 18 18"
          fill="none"
          aria-hidden="true"
          class="size-[18px] shrink-0"
        >
          <path
            d="M9 16.5C13.125 16.5 16.5 13.125 16.5 9C16.5 4.875 13.125 1.5 9 1.5C4.875 1.5 1.5 4.875 1.5 9C1.5 13.125 4.875 16.5 9 16.5Z"
            stroke="currentColor"
            stroke-width="1.125"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M9 8.25V12"
            stroke="currentColor"
            stroke-width="1.125"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M8.99597 6H9.00271"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span class="text-sm font-bold leading-5 opacity-85">{title()}</span>
        <svg
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          class="size-3 shrink-0 transition-transform duration-200"
          classList={{ 'rotate-90': open() }}
        >
          <path
            d="M4.455 9.96L7.715 6.7C8.1 6.315 8.1 5.685 7.715 5.3L4.455 2.04"
            stroke="currentColor"
            stroke-width="1.02857"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <div
        class="grid transition-[grid-template-rows] duration-200 ease-out"
        classList={{
          'grid-rows-[1fr]': open(),
          'grid-rows-[0fr]': !open(),
        }}
      >
        <ul class="list-disc overflow-hidden px-2 ps-9 text-sm leading-5 text-[#856245] opacity-85 marker:text-[#856245]">
          <For each={bullets()}>{(line) => <li class="py-0.5">{line}</li>}</For>
        </ul>
      </div>
    </div>
  )
}

function RedeemConfirmDialog(props: {
  item: ShopItem
  isRedeemPending: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const name = useLocalizedTreeName(() => props.item.treeTypeId)
  const nameKey = () => `tree_type_${props.item.treeTypeId}_title`
  const treeName = () => (name() === nameKey() ? '' : name())

  return (
    <Portal>
      <div
        role="presentation"
        class="fixed inset-0 z-[60] flex items-center justify-center bg-black/10 px-6 backdrop-blur-[8px]"
        onClick={props.onCancel}
      >
        <div
          role="dialog"
          aria-modal="true"
          class="w-[300px] overflow-hidden rounded-[32px] border-2 border-white/30 bg-white/75"
          onClick={(e) => e.stopPropagation()}
        >
          <div class="flex h-[120px] items-center justify-center">
            <img
              src={props.item.asset}
              alt=""
              aria-hidden="true"
              class="h-[96px] w-[96px] object-contain"
            />
          </div>
          <div class="flex flex-col gap-2 px-6 pt-5 text-center">
            <h2 class="text-lg font-semibold text-[#333333]">
              {m.dailog_redeem_confirmation_title()}
            </h2>
            <p class="whitespace-pre-line text-base text-[#666666]">
              {m.dailog_redeem_confirmation_content({
                number: String(props.item.price ?? '—'),
                tree_name: treeName(),
              })}
            </p>
          </div>
          <div class="grid grid-cols-2 gap-2 p-6 pt-4">
            <button
              type="button"
              class="flex h-[52px] items-center justify-center rounded-xl text-base font-bold text-[#176361]"
              onClick={props.onCancel}
            >
              {m.cancel()}
            </button>
            <button
              type="button"
              class="flex h-[52px] items-center justify-center rounded-xl bg-[#176361] text-base font-bold text-white active:bg-[#0f4d4b] disabled:opacity-70"
              disabled={props.isRedeemPending}
              onClick={props.onConfirm}
            >
              {m.redeem_btn()}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  )
}
